<script setup lang="ts">
import { SwitchComponent } from '@metapic-apps/shared'
import { useFlag } from '@unleash/proxy-client-vue'

import TrafficSources from '@/components/CampaignForm/TrafficSources.vue'
import { FLAG_CAMPAIGN_PAYMENT_SPLIT } from '@/lib/constants/flags.constants'
import type { CampaignFormType } from '@/lib/types/campaign.types'
import { BoxComponent, FlexComponent, InputComponent, TitleComponent, TypographyComponent } from '@/ui-library'

defineProps<{ currency?: string }>()

const isPaymentSplitEnabled = useFlag(FLAG_CAMPAIGN_PAYMENT_SPLIT)

const hasCommission = defineModel<CampaignFormType['hasCommission']>('hasCommission')
const invoiceAdvertiserDirectly = defineModel<CampaignFormType['invoiceAdvertiserDirectly']>('invoiceAdvertiserDirectly')
const trafficSourceCosts = defineModel<CampaignFormType['trafficSourceCosts']>('trafficSourceCosts')
const hasAdditionalPayment = defineModel<CampaignFormType['hasAdditionalPayment']>('hasAdditionalPayment')
const creatorCost = defineModel<CampaignFormType['creatorCost']>('creatorCost')
const hasInvoiceAdvertiserDirectly = defineModel<CampaignFormType['hasInvoiceAdvertiserDirectly']>('hasInvoiceAdvertiserDirectly')
const revenueInvoice = defineModel<CampaignFormType['revenueInvoice']>('revenueInvoice')
</script>

<template>
  <template v-if="!isPaymentSplitEnabled">
    <BoxComponent :mb="24">
      <SwitchComponent v-model="invoiceAdvertiserDirectly" title="Invoice advertiser directly" name="invoiceAdvertiserDirectly">
        <template #tooltip>
          <BoxComponent :w="280">
            <BoxComponent :mb="4">
              <TypographyComponent variant="title-medium">Invoice advertiser directly</TypographyComponent>
            </BoxComponent>
            <TypographyComponent variant="body-medium">
              This option allows you to set invoice costs. These amounts will be charged per click (Invoice CPC) and per order (Invoice CPA)
              and included in the advertiser's invoice.
            </TypographyComponent>
          </BoxComponent>
        </template>
      </SwitchComponent>
    </BoxComponent>
    <BoxComponent :mb="24">
      <SwitchComponent v-model="hasCommission" title="Commission" name="hasCommission" />
    </BoxComponent>
    <BoxComponent v-if="hasCommission && currency" :mb="24">
      <TrafficSources
        :key="currency"
        v-model:traffic-source-costs="trafficSourceCosts"
        :currency="currency"
        :invoice-advertiser-directly="invoiceAdvertiserDirectly"
      />
    </BoxComponent>
    <SwitchComponent v-model="hasAdditionalPayment" title="Fixed fee" name="hasAdditionalPayment" />
    <BoxComponent v-if="hasAdditionalPayment" :mt="24">
      <el-form-item prop="creatorCost">
        <InputComponent v-model.number="creatorCost" name="creatorCost">
          <template v-if="currency" #suffix>
            <TypographyComponent variant="title-large"> {{ currency }} </TypographyComponent>
          </template>
        </InputComponent>
      </el-form-item>
    </BoxComponent>
  </template>
  <template v-else>
    <BoxComponent :mb="24">
      <SwitchComponent v-model="hasCommission" title="Commission" name="hasCommission" />
    </BoxComponent>
    <BoxComponent v-if="hasCommission && currency" :mb="24" :p="16" class="section-box">
      <BoxComponent :mb="24">
        <SwitchComponent v-model="invoiceAdvertiserDirectly" title="Invoice advertiser directly" name="invoiceAdvertiserDirectly">
          <template #tooltip>
            <BoxComponent :w="280">
              <BoxComponent :mb="4">
                <TypographyComponent variant="title-medium">Invoice advertiser directly</TypographyComponent>
              </BoxComponent>
              <TypographyComponent variant="body-medium">
                This option allows you to set invoice costs. These amounts will be charged per click (Invoice CPC) and per order (Invoice
                CPA) and included in the advertiser's invoice.
              </TypographyComponent>
            </BoxComponent>
          </template>
        </SwitchComponent>
      </BoxComponent>
      <TrafficSources
        :key="currency"
        v-model:traffic-source-costs="trafficSourceCosts"
        :currency="currency"
        :invoice-advertiser-directly="invoiceAdvertiserDirectly"
      />
    </BoxComponent>
    <BoxComponent :mb="24">
      <SwitchComponent v-model="hasAdditionalPayment" title="Additional payment" name="hasAdditionalPayment" />
    </BoxComponent>
    <BoxComponent v-if="hasAdditionalPayment" :p="16" class="section-box">
      <BoxComponent :mb="24">
        <SwitchComponent v-model="hasInvoiceAdvertiserDirectly" title="Invoice advertiser directly" name="hasInvoiceAdvertiserDirectly">
          <template #tooltip>
            <BoxComponent :w="280">
              <BoxComponent :mb="4">
                <TypographyComponent variant="title-medium">Invoice advertiser directly</TypographyComponent>
              </BoxComponent>
              <TypographyComponent variant="body-medium">
                This option allows you to invoice the advertiser a different amount than the creator earns. When off, the advertiser is
                invoiced the same amount the creator is awarded.
              </TypographyComponent>
            </BoxComponent>
          </template>
        </SwitchComponent>
      </BoxComponent>
      <FlexComponent :gap="16" wrap="wrap">
        <div class="fee-input" :class="{ 'fee-input--half': hasInvoiceAdvertiserDirectly }">
          <el-form-item prop="creatorCost">
            <InputComponent v-model.number="creatorCost" name="creatorCost">
              <template #header>
                <BoxComponent :mb="8">
                  <TitleComponent title="Creator earnings" />
                </BoxComponent>
              </template>
              <template #suffix>
                <TypographyComponent v-if="currency" variant="title-large"> {{ currency }} </TypographyComponent>
              </template>
            </InputComponent>
          </el-form-item>
        </div>
        <div v-if="hasInvoiceAdvertiserDirectly" class="fee-input fee-input--half">
          <el-form-item prop="revenueInvoice">
            <InputComponent v-model.number="revenueInvoice" name="revenueInvoice">
              <template #header>
                <BoxComponent :mb="8">
                  <TitleComponent title="Advertiser invoice" />
                </BoxComponent>
              </template>
              <template #suffix>
                <TypographyComponent v-if="currency" variant="title-large"> {{ currency }} </TypographyComponent>
              </template>
            </InputComponent>
          </el-form-item>
        </div>
      </FlexComponent>
    </BoxComponent>
  </template>
</template>

<style scoped lang="scss">
.section-box {
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.04);
}
.fee-input {
  width: 100%;
}
.fee-input--half {
  width: calc(50% - 8px);
}
</style>
